import React, { useState } from 'react';
import axios from 'axios';
import PreviewCanvas from '../PreviewCanvas';
import './AdvancedPDFExportModal.css';

const EXPORT_MODES = [
  {
    key: 'mini',
    label: 'Mini',
    subtitle: '2 copies per sheet',
    columns: 2,
    rows: 1,
    scale: 0.5,
    copiesPerSheet: 2,
    layout: 'two-up',
  },
  {
    key: 'normal',
    label: 'Normal',
    subtitle: '1 sheet',
    columns: 1,
    rows: 1,
    scale: 1,
  },
  {
    key: 'poster',
    label: 'Poster',
    subtitle: '4 sheets',
    columns: 2,
    rows: 2,
    scale: 2,
  },
  {
    key: 'large',
    label: 'Large Poster',
    subtitle: '9 sheets',
    columns: 3,
    rows: 3,
    scale: 3,
  },
  {
    key: 'giant',
    label: 'Giant Poster',
    subtitle: '16 sheets',
    columns: 4,
    rows: 4,
    scale: 4,
  },
];

const AdvancedPDFExportModal = ({
  pages,
  onClose,
  onExport,
  exportFilename,
  setExportFilename,
}) => {
  const [selectedMode, setSelectedMode] = useState(EXPORT_MODES[1]);
  const [printerSafe, setPrinterSafe] = useState(false);

  const trackLoginAction = async (action) => {
    try {
      const logId = localStorage.getItem('currentLoginLogId');
      if (!logId) return;

      await axios.post('https://edugroove-backend.onrender.com/api/users/log-action', {
        logId,
        action,
      });
    } catch (err) {
      console.error('Failed to track login action:', err);
    }
  };

  const handleTrackedAdvancedPDFExport = async () => {
    await trackLoginAction('advancedPdfExport');
    onExport({ ...selectedMode, printerSafe });
  };

  return (
    <div className="advanced-pdf-overlay">
      <div className="advanced-pdf-modal">
        <div className="advanced-pdf-header">
          <div>
            <h2>Advanced PDF Export</h2>
            <p>Choose how your worksheet will be split across printable A4 pages.</p>
          </div>

          <button className="advanced-pdf-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="advanced-pdf-body">
          <div className="advanced-pdf-options">            

            {EXPORT_MODES.map((mode) => (
              <button
                key={mode.key}
                className={`advanced-pdf-option ${
                  selectedMode.key === mode.key ? 'active' : ''
                }`}
                onClick={() => setSelectedMode(mode)}
              >
                <strong>{mode.label}</strong>
                <span>{mode.subtitle}</span>

                <div
                  className="advanced-pdf-mini-grid"
                  style={{
                    gridTemplateColumns: `repeat(${mode.columns}, 1fr)`,
                    gridTemplateRows: `repeat(${mode.rows}, 1fr)`,
                  }}
                >
                  {Array.from({ length: mode.columns * mode.rows }).map((_, index) => (
                    <div key={index} />
                  ))}
                </div>
              </button>
            ))}
          </div>

          <div className="advanced-pdf-preview-area">
            <h3>Visual Preview</h3>
            <p className="advanced-pdf-help">
              The gaps show where the A4 sheets will be separated when printed.
            </p>

            <div className="advanced-pdf-pages-preview">
              {pages.map((page, pageIndex) => {
                const previewPageWidth = 520;
                const previewPageHeight =
                  ((page.height || 794) / (page.width || 1123)) * previewPageWidth;

                const isMiniMode = selectedMode.key === 'mini';

                if (isMiniMode) {
                  const isPortraitMini = page.orientation === 'portrait';
                  const safeScale = printerSafe ? 0.9 : 1;

                  // Mini should use the opposite output sheet orientation:
                  // portrait worksheet  -> landscape sheet, 2 copies side by side
                  // landscape worksheet -> portrait sheet, 2 copies stacked
                  const miniSheetWidth = isPortraitMini ? previewPageHeight : previewPageHeight;
                  const miniSheetHeight = isPortraitMini ? previewPageWidth : previewPageWidth;

                  const slotWidth = isPortraitMini ? miniSheetWidth / 2 : miniSheetWidth;
                  const slotHeight = isPortraitMini ? miniSheetHeight : miniSheetHeight / 2;

                  const flexDirection = isPortraitMini ? 'row' : 'column';

                  return (
                    <div key={page.id || pageIndex} className="advanced-pdf-page-block">
                      <div className="advanced-pdf-page-title">
                        Page {pageIndex + 1} — Mini copies
                      </div>

                      <div
                        className="advanced-pdf-mini-sheet-preview"
                        style={{
                          width: miniSheetWidth,
                          height: miniSheetHeight,
                          flexDirection,
                        }}
                      >
                        {[0, 1].map((copyIndex) => {
                          const fittedScale = Math.min(
                            slotWidth / previewPageWidth,
                            slotHeight / previewPageHeight
                          );

                          const finalScale = fittedScale * safeScale;

                          return (
                            <div
                              key={copyIndex}
                              className="advanced-pdf-mini-copy-slot"
                              style={{
                                width: slotWidth,
                                height: slotHeight,
                              }}
                            >
                              <div className="advanced-pdf-tile-number">
                                {copyIndex + 1}
                              </div>

                              <div
                                className="advanced-pdf-mini-copy-content"
                                style={{
                                  width: previewPageWidth,
                                  height: previewPageHeight,
                                  left: '50%',
                                  top: '50%',
                                  transform: `translate(-50%, -50%) scale(${finalScale})`,
                                  transformOrigin: 'center center',
                                }}
                              >
                                <PreviewCanvas
                                  page={page}
                                  maxWidth={previewPageWidth}
                                  maxHeight={previewPageHeight}
                                />
                              </div>

                              {printerSafe && <div className="advanced-pdf-cut-box" />}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                const effectiveColumns =
                  selectedMode.orientationAware && page.orientation === 'portrait'
                    ? 1
                    : selectedMode.columns;

                const effectiveRows =
                  selectedMode.orientationAware && page.orientation === 'portrait'
                    ? 2
                    : selectedMode.rows;

                const tileWidth = previewPageWidth / effectiveColumns;
                const tileHeight = previewPageHeight / effectiveRows;

                return (
                  <div key={page.id || pageIndex} className="advanced-pdf-page-block">
                    <div className="advanced-pdf-page-title">
                      Page {pageIndex + 1}
                    </div>

                    <div
                      className="advanced-pdf-tile-grid"
                      style={{
                        gridTemplateColumns: `repeat(${effectiveColumns}, ${tileWidth}px)`,
                        gridTemplateRows: `repeat(${effectiveRows}, ${tileHeight}px)`,
                      }}
                    >
                      {Array.from({
                        length: effectiveColumns * effectiveRows,
                      }).map((_, tileIndex) => {
                        const col = tileIndex % effectiveColumns;
                        const row = Math.floor(tileIndex / effectiveColumns);

                        return (
                          <div
                            key={tileIndex}
                            className="advanced-pdf-tile"
                            style={{
                              width: tileWidth,
                              height: tileHeight,
                            }}
                          >
                            <div className="advanced-pdf-tile-number">
                              {tileIndex + 1}
                            </div>

                            <div
                              className="advanced-pdf-tile-content"
                              style={{
                                width: tileWidth,
                                height: tileHeight,
                                transform: printerSafe ? 'scale(0.9)' : 'scale(1)',
                              }}
                            >
                              <div
                                className="advanced-pdf-tile-inner"
                                style={{
                                  width: previewPageWidth,
                                  height: previewPageHeight,
                                  transform: `translate(${-col * tileWidth}px, ${-row * tileHeight}px)`,
                                  transformOrigin: 'top left',
                                }}
                              >
                                <PreviewCanvas
                                  page={page}
                                  maxWidth={previewPageWidth}
                                  maxHeight={previewPageHeight}
                                />
                              </div>
                            </div>

                            {printerSafe && <div className="advanced-pdf-cut-box" />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="advanced-pdf-footer">
          <div className="advanced-pdf-footer-left">
            <label className="advanced-pdf-checkbox-row">
              <input
                type="checkbox"
                checked={printerSafe}
                onChange={(e) => setPrinterSafe(e.target.checked)}
              />
              <span>
                <strong>Printer safe</strong>
                <small>Inset artwork by 5mm and add faint cut lines</small>
              </span>
            </label>
          </div>

          <div className="advanced-pdf-footer-right">
            <div className="advanced-pdf-footer-filename">
              <label htmlFor="advanced-pdf-filename">
                File Name
              </label>

              <input
                id="advanced-pdf-filename"
                type="text"
                value={exportFilename || ''}
                onChange={(e) => setExportFilename(e.target.value)}
                placeholder="Enter file name"
                className="advanced-pdf-filename-input"
              />
            </div>

            <button className="advanced-pdf-cancel" onClick={onClose}>
              Cancel
            </button>

            <button
              className="advanced-pdf-export"
              onClick={handleTrackedAdvancedPDFExport}
            >
              Export {selectedMode.label}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedPDFExportModal;